import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CardContent, CardHeader } from "../../components/ui/card";
import { Check, CreditCard, ArrowLeft, Loader2, CheckCircle, ExternalLink, X, Send, AlertTriangle } from "lucide-react";
import StripePaymentForm from "../Payment/StripePaymentForm";
import PaystackPaymentForm from "../Payment/PaystackPaymentForm";
import { Elements } from "@stripe/react-stripe-js";
import stripePromise from "../../config/stripe";
import { getPaymentPlans, getSubscription, requestCustomPlan, cancelSubscription, validateDiscount } from "../../services/paymentService";
import { getPaystackPaymentPlans } from "../../services/paystackService";
import { shouldUsePaystack, getUserCountryCode, getUserRegion } from "../../utils/locationUtils";
import {
  formatPrice,
  validateStripePlan,
  createFallbackPlan,
  isEnterprisePlan,
  getCardBackgroundClass,
  hasRequiredMetadata,
  extractTokensFromPlan,
  formatPlanForDisplay
} from "../../utils/stripeHelpers";

const UpgradePlan = ({ onBack }) => {
  const { token, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usePaystack, setUsePaystack] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [userCountry, setUserCountry] = useState('US');
  // eslint-disable-next-line no-unused-vars
  const [userRegion, setUserRegion] = useState('United States');
  const [locationLoading, setLocationLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [enterpriseFormData, setEnterpriseFormData] = useState({
    subject: '',
    description: ''
  });
  const [enterpriseFormErrors, setEnterpriseFormErrors] = useState({});
  const [isSubmittingEnterprise, setIsSubmittingEnterprise] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Discount state
  const [discountCode, setDiscountCode] = useState('');
  const [discountData, setDiscountData] = useState(null);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  const [discountSuccess, setDiscountSuccess] = useState(false);
  // Helper function to format Paystack pricing data
  const formatPaystackPricingData = (paystackPlans) => {
    return paystackPlans.map(plan => {
      // Format plan for display with proper fallbacks
      const formattedPlan = formatPlanForDisplay(plan);

      // Validate plan data
      const validation = validateStripePlan(plan);
      if (!validation.isValid) {
        console.warn('Invalid plan data:', plan, validation.errors);
      }

      // Check if plan has required metadata
      const hasValidMetadata = hasRequiredMetadata(plan);
      if (!hasValidMetadata) {
        console.warn('Plan missing required metadata:', plan);
      }

      // Extract tokens for display
      const tokens = extractTokensFromPlan(plan);
      const displayTokens = tokens > 0 ? tokens.toLocaleString() : 'Unlimited';

      // Check if plan is enterprise plan or development support plan
      const planNameLower = (formattedPlan.name || "").toLowerCase();
      const isEnterprise = planNameLower.includes("enterprise");
      const isDevelopmentSupport = planNameLower.includes("development support") || planNameLower.includes("api integration");

      // Format price for enterprise/development support plans
      let formattedPrice;
      if (isEnterprise) {
        // Convert $299 to NGN (approximately 1 USD = 1400 NGN)
        const nairaAmount = Math.round(299 * 1400);
        formattedPrice = `₦${nairaAmount.toLocaleString()}+`;
      } else if (isDevelopmentSupport) {
        // Development Support plans show "Custom"
        formattedPrice = 'Custom';
      } else {
        formattedPrice = plan.price ? `₦${(plan.price / 100).toLocaleString()}` : 'Custom';
      }

      // Handle features - for development support, use plan features or default
      let features = plan.features || plan.metadata?.features?.split(',') || [];
      if (features.length === 0 && !isDevelopmentSupport) {
        features = [
          `${displayTokens} Tokens`,
          `${Math.round(tokens * 4).toLocaleString()} Characters`
        ];
      } else if (features.length === 0 && isDevelopmentSupport) {
        features = ['Dedicated integration support from NKENNEAi engineers, including onboarding guidance, technical reviews, and personalized implementation assistance.'];
      }

      // Add Enterprise-specific feature if it's an Enterprise plan
      if (isEnterprise) {
        const priorityAccessFeature = 'Priority access to NKENNEAi Product and Engineering teams';
        // Ensure the feature is not already in the list (case-insensitive check)
        const hasPriorityAccess = features.some(f =>
          f.toLowerCase().includes('priority access') ||
          f.toLowerCase().includes('product and engineering')
        );
        if (!hasPriorityAccess) {
          features = [...features, priorityAccessFeature];
        }
      }

      return {
        id: plan.stripePriceId || plan.id,
        name: formattedPlan.name,
        price: formattedPrice,
        amount: plan.price || null, // Amount in kobo for Paystack
        period: plan.interval ? `Per ${plan.interval}` : 'Per purchase',
        features: features,
        buttonText: plan.buttonText || (isEnterprise || isDevelopmentSupport ? 'Contact Us' : 'Select Plan'),
        cardBg: getCardBackgroundClass(plan),
        // Include Paystack-specific data for backend processing
        stripePriceId: plan.stripePriceId || plan.id,
        stripeProductId: plan.stripeProductId || plan.productId,
        paystackPlanId: plan.paystackPlanId || plan.id || plan.plan_code,
        tokens: tokens,
        displayTokens: displayTokens,
        currency: 'NGN', // Paystack uses NGN
        isActive: plan.isActive !== false, // Default to true if not specified
        hasValidMetadata: hasValidMetadata,
        metadata: plan.metadata || {}
      };
    });
  };

  // Helper function to format Stripe pricing data
  const formatStripePricingData = (stripePlans) => {
    return stripePlans.map(plan => {
      // Format plan for display with proper fallbacks
      const formattedPlan = formatPlanForDisplay(plan);

      // Validate plan data
      const validation = validateStripePlan(plan);
      if (!validation.isValid) {
        console.warn('Invalid plan data:', plan, validation.errors);
      }

      // Check if plan has required metadata
      const hasValidMetadata = hasRequiredMetadata(plan);
      // Extract tokens for display
      const tokens = extractTokensFromPlan(plan);
      const displayTokens = tokens > 0 ? tokens.toLocaleString() : 'Unlimited';

      // Check if plan is enterprise plan or development support plan
      const planNameLower = (formattedPlan.name || "").toLowerCase();
      const isEnterprise = planNameLower.includes("enterprise");
      const isDevelopmentSupport = planNameLower.includes("development support") || planNameLower.includes("api integration");

      // Format price for enterprise/development support plans
      let formattedPrice;
      if (isEnterprise) {
        formattedPrice = "$299+";
      } else if (isDevelopmentSupport) {
        // Development Support plans show "Custom"
        formattedPrice = 'Custom';
      } else {
        formattedPrice = plan.price ? formatPrice(plan.price * 100, plan.currency) : 'Custom';
      }

      // Handle features - for development support, use plan features or default
      let features = plan.features || plan.metadata?.features?.split(',') || [];
      if (features.length === 0 && !isDevelopmentSupport) {
        features = [
          `${displayTokens} Tokens`,
          `${Math.round(tokens * 4).toLocaleString()} Characters`
        ];
      } else if (features.length === 0 && isDevelopmentSupport) {
        features = ['Dedicated integration support from NKENNEAi engineers, including onboarding guidance, technical reviews, and personalized implementation assistance.'];
      }

      // Add Enterprise-specific feature if it's an Enterprise plan
      if (isEnterprise) {
        const priorityAccessFeature = 'Priority access to NKENNEAi Product and Engineering teams';
        // Ensure the feature is not already in the list (case-insensitive check)
        const hasPriorityAccess = features.some(f =>
          f.toLowerCase().includes('priority access') ||
          f.toLowerCase().includes('product and engineering')
        );
        if (!hasPriorityAccess) {
          features = [...features, priorityAccessFeature];
        }
      }

      return {
        id: plan.stripePriceId || plan.id,
        name: formattedPlan.name,
        price: formattedPrice,
        amount: plan.price ? plan.price * 100 : null, // Convert to cents for Stripe
        period: plan.interval ? `Per ${plan.interval}` : 'Per purchase',
        features: features,
        buttonText: plan.buttonText || (isEnterprise || isDevelopmentSupport ? 'Contact Us' : 'Select Plan'),
        cardBg: getCardBackgroundClass(plan),
        // Include Stripe-specific data for backend processing
        stripePriceId: plan.stripePriceId || plan.id,
        stripeProductId: plan.stripeProductId || plan.productId,
        tokens: tokens,
        displayTokens: displayTokens,
        currency: plan.currency || 'usd',
        isActive: plan.isActive !== false, // Default to true if not specified
        hasValidMetadata: hasValidMetadata,
        metadata: plan.metadata || {}
      };
    });
  };

  // Detect user location and determine payment provider
  useEffect(() => {
    const detectLocationAndProvider = async () => {
      try {
        setLocationLoading(true);
        const shouldUsePaystackProvider = await shouldUsePaystack();
        const countryCode = await getUserCountryCode();
        const region = await getUserRegion();

        setUsePaystack(shouldUsePaystackProvider);
        setUserCountry(countryCode);
        setUserRegion(region);
      } catch (err) {
        console.warn('Location detection failed:', err);
        // Default to Stripe if location detection fails
        setUsePaystack(false);
        setUserCountry('US');
        setUserRegion('United States');
      } finally {
        setLocationLoading(false);
      }
    };

    detectLocationAndProvider();
  }, []);

  // Handle Paystack Redirect Callback
  useEffect(() => {
    const verifyTransaction = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const reference = urlParams.get('reference') || urlParams.get('trxref');

      console.log('🔍 Checking for Paystack callback:', {
        reference,
        hasToken: !!token,
        search: window.location.search
      });

      if (reference && token) {
        console.log('✅ Found reference and token, verifying payment...');

        try {
          setLoading(true);
          setIsVerifying(true);

          // Clear params from URL now that we've captured them and set isVerifying
          window.history.replaceState({}, document.title, window.location.pathname);

          // Import verifyPaystackPayment dynamically if needed or ensure it's in imports
          const { verifyPaystackPayment } = await import('../../services/paystackService');

          const result = await verifyPaystackPayment(reference, token);

          if (result) {
            console.log('✅ Payment verification successful:', result);
            // Need to reconstruct a "plan" object or get it from result if possible
            // But handlePaymentSuccess mostly cares about the result
            handlePaymentSuccess(result);

            // If we can identify the plan from the result (e.g. metadata), we should set selectedPlan
            // for the success UI to populate correctly.
            if (result.metadata && result.metadata.plan_name) {
              setSelectedPlan({
                name: result.metadata.plan_name,
                price: result.amount ? `₦${(result.amount / 100).toLocaleString()}` : "Top-up",
                displayTokens: result.metadata.tokens
              });
              setShowSuccessPage(true); // Explicitly show success page
            }
          }
        } catch (verifyError) {
          console.error('Payment verification failed:', verifyError);
          alert('Payment verification failed. Please contact support if you were debited.');
        } finally {
          setLoading(false);
          setIsVerifying(false);
        }
      } else if (reference && !token) {
        console.warn('⚠️ Found reference but NO TOKEN. Authentication might be lost on redirect.');
      }
    };

    if (token || window.location.search.includes('reference')) {
      verifyTransaction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch subscription information
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!token) return;

      try {
        setSubscriptionLoading(true);
        const response = await getSubscription(token);
        if (response.success && response.hasSubscription) {
          setSubscription(response.subscription);
        } else {
          setSubscription(null);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        // Don't show error to user, just set subscription to null
        setSubscription(null);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    if (token && !locationLoading) {
      fetchSubscription();
    }
  }, [token, locationLoading]);

  // Fetch pricing plans based on payment provider
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoading(true);
        setError(null);

        let plans;
        if (usePaystack) {
          // Fetch Paystack plans (in NGN)
          plans = await getPaystackPaymentPlans(token);
        } else {
          // Fetch Stripe plans (in USD)
          plans = await getPaymentPlans(token);
          // plans = await getPaystackPaymentPlans(token);

        }

        // Format the plans to ensure they have the expected structure
        const formattedPlans = usePaystack
          ? formatPaystackPricingData(plans)
          : formatStripePricingData(plans);

        // Filter out Free Playground plan
        const filteredPlans = formattedPlans.filter(plan => {
          const planNameLower = (plan.name || "").toLowerCase();
          return !planNameLower.includes("free playground") &&
            !(planNameLower.includes("free") && (plan.price === "$0" || plan.price === "₦0" || plan.price === 0));
        });

        // Sort plans by price (lowest first)
        const sortedPlans = filteredPlans.sort((a, b) => {
          // Extract numeric price from formatted price strings
          const extractNumericPrice = (priceStr) => {
            if (!priceStr) return Infinity; // Custom/unknown prices go to end

            // Extract number from strings like "$10", "₦1000", "$299+", "₦418,600+", etc.
            // Remove all non-digit characters except decimal point, then parse
            const numericStr = priceStr.replace(/[^\d.]/g, '');
            const num = parseFloat(numericStr);

            // If includes "+" (like $299+), extract the number before the +
            // This allows enterprise plans to sort correctly among themselves
            if (priceStr.includes('+')) {
              return isNaN(num) ? Infinity : num;
            }

            return isNaN(num) ? Infinity : num;
          };

          const priceA = extractNumericPrice(a.price);
          const priceB = extractNumericPrice(b.price);

          return priceA - priceB;
        });

        setPricingPlans(sortedPlans);
      } catch (err) {
        console.error('Error fetching pricing plans:', err);
        setError('Failed to load pricing plans. Please try again later.');
        // Fallback to hardcoded plans if API fails
        setPricingPlans(getFallbackPlans());
      } finally {
        setLoading(false);
      }
    };

    if (token && !locationLoading) {
      fetchPricingPlans();
    } else if (!token) {
      // If no token, use fallback plans
      setPricingPlans(getFallbackPlans());
      setLoading(false);
    }
  }, [token, usePaystack, locationLoading]);

  // Fallback plans in case API fails
  const getFallbackPlans = () => {
    const plans = [
      createFallbackPlan('starter'),
      createFallbackPlan('pro'),
      createFallbackPlan('advanced'),
      createFallbackPlan('enterprise')
    ];

    // Sort fallback plans by price (lowest first)
    return plans.sort((a, b) => {
      // Extract numeric price from formatted price strings
      const extractNumericPrice = (priceStr) => {
        if (!priceStr) return Infinity;

        // Extract number from strings like "$10", "$299+", etc.
        const numericStr = priceStr.replace(/[^\d.]/g, '');
        const num = parseFloat(numericStr);

        // If includes "+" (like $299+), extract the number before the +
        if (priceStr.includes('+')) {
          return isNaN(num) ? Infinity : num;
        }

        return isNaN(num) ? Infinity : num;
      };

      const priceA = extractNumericPrice(a.price);
      const priceB = extractNumericPrice(b.price);

      return priceA - priceB;
    });
  };

  // Helper function to check if a plan is the active subscription
  const isActivePlan = (plan) => {
    if (!subscription || subscription.status !== 'active') {
      return false;
    }
    // Match by plan name (case-insensitive)
    const subscriptionPlanName = subscription.plan?.toLowerCase().trim();
    const currentPlanName = plan.name?.toLowerCase().trim();
    return subscriptionPlanName === currentPlanName;
  };

  // Helper function to check if a plan can be reselected (next billing date has passed)
  const canReselectPlan = (plan) => {
    if (!isActivePlan(plan)) {
      return true; // Not an active plan, so it can be selected
    }

    // If it's an active plan, check if next billing date has passed
    if (subscription?.nextBillingDate) {
      const nextBillingDate = new Date(subscription.nextBillingDate);
      const now = new Date();
      return now >= nextBillingDate;
    }

    // If no next billing date, assume it's a one-time purchase and can't be reselected
    return false;
  };

  const handlePlanSelection = (plan) => {
    // Don't allow selection of active plan until next billing date
    if (isActivePlan(plan) && !canReselectPlan(plan)) {
      const nextBillingDate = subscription?.nextBillingDate
        ? new Date(subscription.nextBillingDate).toLocaleDateString()
        : 'the next billing period';
      alert(`You cannot change your plan until ${nextBillingDate}. This plan is currently active.`);
      return;
    }

    // Check if plan is enterprise or development support
    const planNameLower = (plan.name || "").toLowerCase();
    const isDevelopmentSupport = planNameLower.includes("development support") || planNameLower.includes("api integration");

    if (isEnterprisePlan(plan) || isDevelopmentSupport) {
      setSelectedPlan(plan);
      setShowEnterpriseModal(true);
      return;
    }

    // Clear discount state when selecting a new plan to prevent cross-plan discount leakage
    setDiscountCode('');
    setDiscountData(null);
    setDiscountError(null);
    setDiscountSuccess(false);

    setSelectedPlan(plan);
    setShowPaymentForm(true);
  };

  const handleEnterpriseFormChange = (e) => {
    const { name, value } = e.target;
    setEnterpriseFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (enterpriseFormErrors[name]) {
      setEnterpriseFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEnterpriseFormSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = {};
    if (!enterpriseFormData.subject || enterpriseFormData.subject.trim().length < 5) {
      errors.subject = 'Subject must be at least 5 characters long';
    }
    if (!enterpriseFormData.description || enterpriseFormData.description.trim().length < 10) {
      errors.description = 'Description must be at least 10 characters long';
    }

    if (Object.keys(errors).length > 0) {
      setEnterpriseFormErrors(errors);
      return;
    }

    setIsSubmittingEnterprise(true);
    setEnterpriseFormErrors({});

    try {
      // Request custom plan using the payment service
      await requestCustomPlan(
        enterpriseFormData.subject,
        enterpriseFormData.description,
        token
      );

      // Reset form and close modal
      setEnterpriseFormData({ subject: '', description: '' });
      setShowEnterpriseModal(false);
      setSelectedPlan(null);

      // Show success message
      alert('Your custom plan request has been submitted successfully! Our sales team will contact you soon.');
    } catch (error) {
      console.error('Error submitting custom plan request:', error);
      setEnterpriseFormErrors({ submit: error.message || 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmittingEnterprise(false);
    }
  };

  const handleCloseEnterpriseModal = () => {
    setShowEnterpriseModal(false);
    setEnterpriseFormData({ subject: '', description: '' });
    setEnterpriseFormErrors({});
    setSelectedPlan(null);
  };

  const handlePaymentSuccess = (result) => {
    console.log('✅ Payment success result:', result);

    // Verify payment status is success before proceeding
    const paymentStatus = result?.paymentStatus || 'success';

    if (paymentStatus !== 'success') {
      console.warn('Payment status is not success:', paymentStatus);
      // This shouldn't happen if StripePaymentForm is handling status correctly
      // But handle it gracefully just in case
      if (paymentStatus === 'processing') {
        alert('Your payment is being processed. You will be notified once it is completed.');
        setShowPaymentForm(false);
        return;
      }
      if (paymentStatus === 'failed') {
        alert('Payment failed. Please try again or contact support.');
        setShowPaymentForm(false);
        return;
      }
    }

    // Update user tokens in Redux if result contains token information
    if (result && user) {
      // Calculate new token balance
      // If result.tokens is provided and > 0, use it (backend returned updated balance)
      // Otherwise, calculate: current tokens + tokens added
      let newTokenBalance;
      if (result.tokens !== undefined && result.tokens > 0) {
        newTokenBalance = result.tokens;
      } else if (result.tokensAdded) {
        // Backend returned tokens: 0 but tokensAdded, so calculate
        newTokenBalance = (user.tokens || 0) + result.tokensAdded;
      } else {
        // Fallback to current tokens
        newTokenBalance = user.tokens || 0;
      }

      const updatedUser = {
        ...user,
        tokens: newTokenBalance,
      };

      // If subscription data is present, add it to user
      if (result.subscription) {
        updatedUser.subscription = result.subscription;
        console.log('✅ Subscription data received:', result.subscription);
      }

      // Update Redux store


      console.log('✅ User state updated:', {
        previousTokens: user.tokens || 0,
        tokensAdded: result.tokensAdded || 0,
        newTokens: newTokenBalance,
        hasSubscription: !!result.subscription,
        paymentStatus: paymentStatus
      });
    }

    // Show success page instead of alert
    setShowPaymentForm(false);
    setShowSuccessPage(true);

    // Refresh subscription data
    getSubscription(token).then(response => {
      if (response.success && response.hasSubscription) {
        setSubscription(response.subscription);
      }
    }).catch(err => {
      console.error('Error refreshing subscription:', err);
    });
  };

  const handlePaymentError = (error) => {
    alert(`Payment failed: ${error.message}`);
    setShowPaymentForm(false);
  };

  const handleBack = () => {
    if (showPaymentForm) {
      setShowPaymentForm(false);
      setSelectedPlan(null);
      setDiscountCode('');
      setDiscountData(null);
      setDiscountError(null);
      setDiscountSuccess(false);
    } else if (showSuccessPage) {
      setShowSuccessPage(false);
      setSelectedPlan(null);
      setDiscountCode('');
      setDiscountData(null);
      setDiscountError(null);
      setDiscountSuccess(false);
    } else {
      onBack && onBack();
    }
  };

  const handleViewPurchaseHistory = () => {
    navigate('/user-dashboard/purchase-history');
  };

  const handleViewDeveloperTools = () => {
    navigate('/user-dashboard/developer-tools');
  };

  const handleBackToPlans = () => {
    setShowSuccessPage(false);
    setSelectedPlan(null);
    setDiscountCode('');
    setDiscountData(null);
    setDiscountError(null);
    setDiscountSuccess(false);
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    const confirmMessage = `Are you sure you want to cancel your subscription? It will remain active until ${subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString() : 'the end of the billing period'}, and you will continue to have access until then.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancelSubscription(false, token);

      // Refresh subscription data
      const response = await getSubscription(token);
      if (response.success && response.hasSubscription) {
        setSubscription(response.subscription);
      } else {
        setSubscription(null);
      }

      setShowCancelModal(false);
      alert('Your subscription will be cancelled at the end of the billing period.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert(`Failed to cancel subscription: ${error.message || 'Please try again later.'}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountCode('');
    setDiscountData(null);
    setDiscountError(null);
    setDiscountSuccess(false);
  };

  const handleApplyDiscountInPaymentFlow = async (code) => {
    if (!code.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    setIsApplyingDiscount(true);
    setDiscountError(null);
    setDiscountSuccess(false);

    try {
      let numericPrice;
      const priceStr = String(selectedPlan.amount || selectedPlan.price);

      if (usePaystack) {
        numericPrice = (selectedPlan.amount ? selectedPlan.amount / 100 : parseFloat(priceStr.replace(/[^0-9.]/g, '')));
      } else {
        numericPrice = selectedPlan.amount ? selectedPlan.amount / 100 : parseFloat(priceStr.replace(/[^0-9.]/g, ''));
      }

      const currency = usePaystack ? 'ngn' : (selectedPlan.currency || 'usd');

      if (isNaN(numericPrice) || numericPrice === 0) {
        setDiscountError('Discount cannot be applied to custom or free plans.');
        setIsApplyingDiscount(false);
        return;
      }

      const response = await validateDiscount(
        code.trim(),
        usePaystack ? (selectedPlan.paystackPlanId || selectedPlan.id) : (selectedPlan.stripeProductId || selectedPlan.id),
        numericPrice,
        currency,
        token
      );

      if (response && response.valid) {
        setDiscountData(response);
        setDiscountSuccess(true);
      } else {
        setDiscountError(
          response?.message ||
          response?.error ||
          response?.reason ||
          'Invalid discount code'
        );
      }
    } catch (err) {
      const apiMessage = err.details?.reason || err.details?.error || err.reason;
      setDiscountError(apiMessage || 'Failed to apply discount code');
    } finally {
      setIsApplyingDiscount(false);
    }
  };


  if (showPaymentForm && selectedPlan) {
    const discountedAmount = discountData ? discountData.discountedPrice : (selectedPlan.amount ? selectedPlan.amount / 100 : 0);

    const planWithDiscount = discountData ? {
      ...selectedPlan,
      discountCode: discountData.code || discountCode,
      discountCodeId: discountData.discountCodeId,
      amount: Math.round(discountedAmount * 100),
      price: usePaystack ? `₦${discountedAmount.toLocaleString()}` : `$${discountedAmount}`
    } : selectedPlan;

    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Column: Order Summary */}
          <div className="space-y-8 pr-0 lg:pr-8">
            <div>
              <p className="text-gray-500 font-medium mb-2">Purchase NKENNEAi Pro Pack</p>
              <h1 className="text-6xl font-black text-black flex items-baseline gap-2">
                {usePaystack ? '₦' : 'US$'}{discountedAmount.toLocaleString()}
                <span className="text-gray-500 text-lg font-normal ml-2">
                  {selectedPlan.period}
                </span>
              </h1>
            </div>

            {/* Plan Card */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex justify-between items-start">
              <div>
                <h3 className="font-bold text-gray-900 text-lg mb-1">{selectedPlan.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Includes {selectedPlan.displayTokens || '550,000'} tokens<br />
                  and {Math.round((selectedPlan.tokens || 0) * 4).toLocaleString() || '2,200,000'} characters
                </p>
              </div>
              <p className="font-bold text-gray-900">
                {usePaystack ? '₦' : 'US$'}{(selectedPlan.amount / 100).toLocaleString()}
              </p>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center text-gray-900 font-bold">
                <span>Subtotal</span>
                <span>{usePaystack ? '₦' : 'US$'}{(selectedPlan.amount / 100).toLocaleString()}</span>
              </div>

              {/* Discount Section */}
              <div className="space-y-3">
                <p className="text-gray-900 font-bold">Discount Code</p>
                {!discountSuccess ? (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="ENTER CODE"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 placeholder-gray-400 focus:ring-2 focus:ring-gray-200"
                      />
                    </div>
                    <button
                      onClick={() => handleApplyDiscountInPaymentFlow(discountCode)}
                      disabled={isApplyingDiscount || !discountCode}
                      className="bg-gray-100 text-gray-500 px-6 py-3 rounded-xl font-bold hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      {isApplyingDiscount ? '...' : 'Apply'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-green-50 text-green-700 px-4 py-3 rounded-xl flex justify-between items-center border border-green-100">
                    <div className="flex items-center gap-2 font-medium">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>
                        {discountData.code} - {
                          discountData.discountType === 'fixed'
                            ? `${discountData.currency === 'usd' ? 'US$' : '₦'}${discountData.discountValue} off`
                            : `${discountData.percentage || discountData.discountValue}% off`
                        }
                      </span>
                    </div>
                    <button
                      onClick={handleRemoveDiscount}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
                {discountError && (
                  <p className="text-red-500 text-sm">{discountError}</p>
                )}
              </div>

              {/* Discount Breakout */}
              {discountSuccess && (
                <div className="space-y-3 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center text-gray-400 font-medium">
                    <span>{selectedPlan.name}</span>
                    <span className="line-through">{usePaystack ? '₦' : 'US$'}{(selectedPlan.amount / 100).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-green-600 font-medium">
                    <span>
                      Discount ({
                        discountData.discountType === 'fixed'
                          ? `${discountData.currency === 'usd' ? 'US$' : '₦'}${discountData.discountValue} off`
                          : `${discountData.percentage || discountData.discountValue}% off`
                      })
                    </span>
                    <span>-{usePaystack ? '₦' : 'US$'}{discountData.discountAmount.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1 pt-6 border-t border-gray-100">
                <div className="flex justify-between items-center text-gray-900 font-bold text-xl">
                  <span>Total due today</span>
                  <span className={discountSuccess ? "text-green-600" : ""}>
                    {usePaystack ? '₦' : 'US$'}{discountedAmount.toLocaleString()}
                  </span>
                </div>
                {discountData?.disclaimer && (
                  <p className="text-gray-500 text-md mt-2 text-left">
                    {discountData.disclaimer}
                    <p className="text-gray-500 text-md mt-2 text-left">
                      Final amount charged in NGN may vary slightly from the displayed amount due to real-time currency conversion rate fluctuations at the time of payment.
                    </p>
                  </p>
                )}
                {selectedPlan.period?.toLowerCase().includes('month') && (
                  <p className="text-gray-400 text-sm text-center mt-4">
                    Applies to first billing cycle only
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Payment Details */}
          <div className="space-y-8">
            {/* Email Box */}
            <div className="bg-gray-50 rounded-2xl p-4 flex justify-between items-center border border-gray-100">
              <span className="text-gray-900 font-bold">Email</span>
              <span className="text-gray-600">{user?.email || 'johndoe205@gmail.com'}</span>
            </div>

            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Payment method</h2>

              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                {/* Method Selector Header (Simplified) */}
                <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full border-4 border-orange-400 flex-shrink-0" />
                  <CreditCard className="w-5 h-5 text-gray-900" />
                  <span className="font-bold text-gray-900">Card</span>
                </div>

                <div className="p-6">
                  {usePaystack ? (
                    <PaystackPaymentForm
                      plan={planWithDiscount}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  ) : (
                    <Elements stripe={stripePromise}>
                      <StripePaymentForm
                        key={`${planWithDiscount.id || planWithDiscount.stripePriceId || planWithDiscount.name || 'plan'}-${planWithDiscount.discountCode || 'no-discount'}`}
                        plan={planWithDiscount}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                      />
                    </Elements>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showSuccessPage && selectedPlan) {
    const discountedAmount = discountData ? discountData.discountedPrice : (selectedPlan.amount ? selectedPlan.amount / 100 : 0);

    const planWithDiscount = discountData ? {
      ...selectedPlan,
      discountCode: discountData.code || discountCode,
      discountCodeId: discountData.discountCodeId,
      amount: Math.round(discountedAmount * 100),
      price: usePaystack ? `₦${discountedAmount.toLocaleString()}` : `$${discountedAmount}`
    } : selectedPlan;

    const finalPrice = discountSuccess ? planWithDiscount.price : selectedPlan.price;

    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Success Page */}
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="mb-8">
            <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-green-100 mb-6 font-black">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-black text-black mb-4 uppercase tracking-tight">
              Payment Successful!
            </h1>
            <p className="text-xl text-gray-500 mb-2">
              Your purchase has been completed successfully
            </p>
            <p className="text-lg text-gray-600">
              You purchased the <span className="font-bold text-black">{selectedPlan.name}</span> for <span className="font-bold text-black">{finalPrice}</span>
            </p>
          </div>

          {/* Purchase Details */}
          <div className="bg-white border border-gray-100 rounded-2xl p-8 mb-8 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-6 text-left border-b border-gray-50 pb-4">Purchase Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div>
                <p className="text-sm font-medium text-gray-400 mb-1">Plan Name</p>
                <p className="font-bold text-gray-900 text-lg">{selectedPlan.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-400 mb-1">Amount Paid</p>
                <p className="font-bold text-gray-900 text-lg">{finalPrice}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-400 mb-1">Tokens Added</p>
                <p className="font-bold text-gray-900 text-lg">{selectedPlan.displayTokens || 'Unlimited'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-400 mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <p className="font-bold text-gray-900">Completed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleViewPurchaseHistory}
                className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View Purchase History
              </button>
              <button
                onClick={handleViewDeveloperTools}
                className="flex items-center justify-center gap-2 bg-secondary text-white px-6 py-3 rounded-lg font-medium hover:bg-secondary/90 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Go to Developer Tools
              </button>
            </div>

            <button
              onClick={handleBackToPlans}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Back to Plans
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Next Steps:</strong> Your tokens have been added to your account.
              You can now start using our translation services or explore our developer tools to integrate our API.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      {/* <div className="text-center mb-12">
        <div className="inline-block text-primary px-4 py-2 text-sm font-medium mb-4 font-semibold">
          UPGRADE YOUR PLAN
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Choose Your Token Package
        </h1>
        <p className="text-gray-600 text-lg max-w-3xl mx-auto">
          Select a plan that fits your translation needs and start translating
          text right away in Yoruba, Swahili, and Nigerian Pidgin today.
        </p>
        
        {!locationLoading && (
          <div className="mt-4 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm">
            <Globe className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800">
              {usePaystack 
                ? `Payment powered by Paystack (${userRegion})`
                : `Payment powered by Stripe (${userRegion})`
              }
            </span>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <CreditCard className="h-6 w-6 text-secondary" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-secondary">
              Current Tokens: {user?.tokens || 0}
            </h3>
            <p className="text-secondary">
              Add more tokens to continue using our translation services.
            </p>
          </div>
        </div>
      </div> */}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CreditCard className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">Error Loading Plans</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Active Subscription Display */}
      {!subscriptionLoading && subscription && subscription.status === 'active' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start flex-1">
              <div className="flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-green-600 mt-1" />
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-green-800">
                    Active Subscription
                  </h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {subscription.status}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-green-600">Plan</p>
                      <p className="font-semibold text-green-800">{subscription.plan}</p>
                    </div>
                    <div>
                      <p className="text-green-600">Amount</p>
                      <p className="font-semibold text-green-800">
                        {subscription.amount
                          ? `${subscription.currency === 'usd' ? '$' : subscription.currency === 'ngn' ? '₦' : ''}${subscription.amount.toLocaleString()}${subscription.interval ? ` / ${subscription.interval}` : ''}`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-600">Tokens</p>
                      <p className="font-semibold text-green-800">
                        {subscription.tokens?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3">
                    <div>
                      <p className="text-green-600">Next Billing Date</p>
                      <p className="font-semibold text-green-800">
                        {subscription.nextBillingDate
                          ? new Date(subscription.nextBillingDate).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-600">Payment Method</p>
                      <p className="font-semibold text-green-800">
                        {subscription.paymentMethod
                          ? `${subscription.paymentMethod.brand?.toUpperCase() || 'Card'} •••• ${subscription.paymentMethod.last4 || 'N/A'}`
                          : 'N/A'}
                      </p>
                    </div>
                    {subscription.cancelAtPeriodEnd && (
                      <div>
                        <p className="text-yellow-600 font-semibold">
                          Cancels at period end
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 ml-4">
              <button
                onClick={() => setShowCancelModal(true)}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
              >
                <AlertTriangle className="w-4 h-4" />
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Loading State */}
      {(loading || locationLoading || subscriptionLoading || isVerifying) && (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-gray-600 font-medium">
              {isVerifying
                ? 'Verifying your payment, please wait...'
                : locationLoading
                  ? 'Detecting your location...'
                  : subscriptionLoading
                    ? 'Loading subscription...'
                    : 'Loading pricing plans...'}
            </span>
          </div>
        </div>
      )}

      {/* Pricing Grid */}
      {!loading && pricingPlans.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
          {pricingPlans.map((plan, index) => {
            const isActive = isActivePlan(plan);
            const canReselect = canReselectPlan(plan);
            const isDisabled = isActive && !canReselect;

            // Get next billing date for display
            const nextBillingDate = isActive && subscription?.nextBillingDate
              ? new Date(subscription.nextBillingDate).toLocaleDateString()
              : null;

            return (
              <div
                key={plan.id || index}
                className={`${plan.cardBg || getCardBackgroundClass(plan)} border-2 rounded-3xl relative overflow-hidden transition-all duration-300 flex flex-col ${isActive ? 'ring-2 ring-green-500 ring-offset-2' : 'hover:shadow-lg hover:scale-105'
                  }`}
              >
                {/* Active Plan Badge */}
                {isActive && (
                  <div className="absolute top-4 right-4 z-10">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Active
                    </span>
                  </div>
                )}

                <CardHeader className="text-left pb-4 border-b-2">
                  <h3 className="font-semibold text-gray-900 text-sm mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-gray-900">
                      {plan.price}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{plan.period}</p>
                </CardHeader>

                <CardContent className="pt-0 mt-6 flex-1 flex flex-col">
                  <div className="mb-6 flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm mb-3">
                      Key Features:
                    </h4>
                    <ul className="space-y-2">
                      {plan.features?.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-center text-sm text-gray-900"
                        >
                          <Check className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Show message if plan is active and can't be reselected */}
                  {isActive && !canReselect && nextBillingDate && (
                    <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-xs text-yellow-800 text-center">
                        Can change after {nextBillingDate}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => handlePlanSelection(plan)}
                    disabled={isDisabled}
                    className={`mt-auto p-2 rounded-md w-full transition-colors ${isDisabled
                      ? 'bg-green-600 text-white cursor-not-allowed opacity-75'
                      : 'bg-black hover:bg-primary/90 text-white'
                      }`}
                  >
                    {isActive
                      ? (canReselect ? 'Change Plan' : 'Selected Plan')
                      : plan.buttonText
                    }
                  </button>
                </CardContent>
              </div>
            );
          })}
        </div>
      )}

      {/* No Plans State */}
      {!loading && pricingPlans.length === 0 && !error && (
        <div className="text-center py-12">
          <CreditCard className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Plans Available</h3>
          <p className="text-gray-600">Please contact support for assistance.</p>
        </div>
      )}

      {/* Enterprise Plan Inquiry Modal */}
      {showEnterpriseModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Enterprise Plan Inquiry</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Request information about {selectedPlan.name}
                </p>
              </div>
              <button
                onClick={handleCloseEnterpriseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleEnterpriseFormSubmit} className="p-6">
              <div className="space-y-6">
                {/* Subject Field */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={enterpriseFormData.subject}
                    onChange={handleEnterpriseFormChange}
                    placeholder="e.g., Enterprise Plan Inquiry for Custom Pricing"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${enterpriseFormErrors.subject ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  />
                  {enterpriseFormErrors.subject && (
                    <p className="mt-1 text-sm text-red-600">{enterpriseFormErrors.subject}</p>
                  )}
                </div>

                {/* Description Field */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={enterpriseFormData.description}
                    onChange={handleEnterpriseFormChange}
                    placeholder="Please provide details about your requirements, expected usage, team size, and any specific features you need..."
                    rows={6}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${enterpriseFormErrors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  />
                  {enterpriseFormErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{enterpriseFormErrors.description}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum 10 characters. Include details about your organization's needs.
                  </p>
                </div>

                {/* Submit Error */}
                {enterpriseFormErrors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-600">{enterpriseFormErrors.submit}</p>
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>What happens next?</strong> Our sales team will review your inquiry and contact you within 24-48 hours to discuss custom pricing and features tailored to your needs.
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseEnterpriseModal}
                  disabled={isSubmittingEnterprise}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingEnterprise}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmittingEnterprise ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Inquiry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && subscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Cancel Subscription</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose how you want to cancel your subscription
                </p>
              </div>
              <button
                onClick={() => setShowCancelModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isCancelling}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">Before you cancel:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Your subscription will remain active until {subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString() : 'the end of the billing period'}</li>
                      <li>You'll continue to have full access until then</li>
                      <li>Any unused tokens will remain in your account</li>
                      <li>You can resubscribe at any time</li>
                    </ul>
                  </div>
                </div>
              </div>

              {subscription.cancelAtPeriodEnd && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <p className="text-sm text-green-800">
                      Your subscription is already set to cancel at the end of the billing period.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Close
              </button>
              {!subscription.cancelAtPeriodEnd && (
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isCancelling ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4" />
                      Cancel Subscription
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Loading Overlay */}
            {isCancelling && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-gray-700">Processing cancellation...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradePlan;
